import { useMutation, useQuery } from "@tanstack/react-query";
import { CheckCircle2, CircleAlert, MapPin } from "lucide-react";
import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { Button, Input, Skeleton } from "@/components/ui";
import { api, ApiError, type PublicAddress } from "@/lib/api";
import { textOn } from "@logiful/domain";

/** Texte der Korrekturseite (Sprache aus dem Lieferland) */
const T = {
  de: {
    title: "Lieferadresse prüfen",
    intro: (n: string) => `Bitte prüfe die Lieferadresse für deine Bestellung ${n}. Sobald sie stimmt, versenden wir.`,
    issues: "Was uns aufgefallen ist:",
    postNumber: "Postnummer (DHL-Kundennummer)", postNumberHint: "Steht in der DHL-App bzw. auf deiner Kundenkarte – Pflicht für die Zustellung an Packstation/Postfiliale.",
    company: "Firma (optional)", firstName: "Vorname", lastName: "Nachname", street: "Straße", houseNumber: "Hausnr.", addition: "Adresszusatz (optional)", zip: "PLZ", city: "Ort", country: "Land",
    check: "Adresse speichern", correct: "Die Adresse ist richtig so",
    suggestion: "Meintest du diese Adresse?", useSuggestion: "Vorschlag übernehmen", keepMine: "Meine Eingabe ist richtig",
    unclear: "Wir konnten die Adresse nicht eindeutig finden.", unclearHint: "Bitte prüfe Straße, Hausnummer und PLZ. Wenn alles stimmt (z. B. Neubaugebiet), kannst du sie trotzdem verwenden.", useAnyway: "Trotzdem so verwenden",
    saved: "Danke! Die Adresse ist gespeichert.", savedHint: "Deine Bestellung geht jetzt auf den Weg.",
    closed: "Diese Adresse wurde bereits geklärt.", closedHint: "Es ist nichts mehr zu tun. Bei Fragen antworte einfach auf unsere E-Mail.",
    notFound: "Link ungültig oder abgelaufen.", required: "Bitte die markierten Felder ausfüllen.", error: "Speichern hat nicht geklappt – bitte noch einmal versuchen.",
    contact: "Fragen?",
  },
  en: {
    title: "Check delivery address",
    intro: (n: string) => `Please check the delivery address for your order ${n}. We'll ship as soon as it's correct.`,
    issues: "What we noticed:",
    postNumber: "DHL Postnummer (customer number)", postNumberHint: "Shown in the DHL app or on your customer card – required for delivery to a Packstation/post office.",
    company: "Company (optional)", firstName: "First name", lastName: "Last name", street: "Street", houseNumber: "No.", addition: "Address line 2 (optional)", zip: "Postcode", city: "City", country: "Country",
    check: "Save address", correct: "The address is correct",
    suggestion: "Did you mean this address?", useSuggestion: "Use suggestion", keepMine: "My entry is correct",
    unclear: "We couldn't find this address exactly.", unclearHint: "Please check street, number and postcode. If everything is right (e.g. a new building), you can still use it.", useAnyway: "Use it anyway",
    saved: "Thank you! The address has been saved.", savedHint: "Your order is now on its way.",
    closed: "This address has already been confirmed.", closedHint: "There's nothing left to do. If you have questions, just reply to our email.",
    notFound: "Link invalid or expired.", required: "Please fill in the highlighted fields.", error: "Saving failed – please try again.",
    contact: "Questions?",
  },
} as const;

type Form = Omit<PublicAddress["address"], "country">;
const REQUIRED: (keyof Form)[] = ["lastName", "street", "zip", "city"];
const ORDER: (keyof Form)[] = ["lastName", "street", "houseNumber", "addition", "zip", "city"];

/** Öffentliche Seite für Endkunden (persönlicher Link aus der Mail) – im Namen des Shops, ohne Anmeldung */
export function PublicAddressPage() {
  const { token = "" } = useParams();
  const q = useQuery({ queryKey: ["public-address", token], queryFn: () => api.publicAddress.get(token), retry: false });
  const [form, setForm] = useState<Form | null>(null);
  const [missing, setMissing] = useState<Set<string>>(new Set());
  // vom Prüfergebnis betroffene Felder: markiert, bis der Kunde sie ändert; das erste bekommt den Fokus
  const [flagged, setFlagged] = useState<Set<string> | null>(null);
  const [state, setState] = useState<{ kind: "edit" } | { kind: "suggestion"; address: Form } | { kind: "unclear"; issues: string[] } | { kind: "saved" }>({ kind: "edit" });
  useEffect(() => { if (q.data && !form) { const { country: _c, ...rest } = q.data.address; setForm(rest); setFlagged(new Set(q.data.fields ?? [])); } }, [q.data, form]);
  const firstFlag = ORDER.find((k) => flagged?.has(k));
  const d = q.data;
  const t = T[d?.lang ?? "de"];
  useEffect(() => { document.title = d ? `${t.title} – ${d.shop}` : t.title; }, [d, t]);
  const submit = useMutation({
    mutationFn: (p: { action: "check" | "confirm"; address: Form }) => api.publicAddress.submit(token, p),
    onSuccess: (r, p) => {
      if (r.result === "saved") setState({ kind: "saved" });
      else if (r.result === "suggestion") setState({ kind: "suggestion", address: { ...p.address, ...r.suggestion } });
      else { setState({ kind: "unclear", issues: r.issues }); if (r.fields?.length) setFlagged(new Set(r.fields)); }
    },
  });
  const dropPoint = !!form && /^(packstation|postfiliale)/i.test(form.street.trim());
  const brand = d?.brandColor ?? null;
  const brandStyle = brand ? { background: brand, color: textOn(brand), borderColor: brand } : undefined;
  const send = (action: "check" | "confirm", address = form!) => {
    const need = /^(packstation|postfiliale)/i.test(address.street.trim()) ? [...REQUIRED, "addition" as const] : REQUIRED;
    const miss = new Set(need.filter((k) => !address[k]?.trim()));
    setMissing(miss);
    if (!miss.size) submit.mutate({ action, address });
  };
  const field = (k: keyof Form, cls = "") => (
    <label className={`block ${cls}`}>
      <span className="text-[12px] font-medium text-fg-muted">{k === "addition" && dropPoint ? t.postNumber : t[k]}</span>
      <Input className="mt-1" value={form?.[k] ?? ""} invalid={missing.has(k) || !!flagged?.has(k)} autoFocus={k === firstFlag} autoComplete={({ company: "organization", firstName: "given-name", lastName: "family-name", street: "address-line1", houseNumber: "address-line1", addition: "address-line2", zip: "postal-code", city: "address-level2" } as const)[k]} onChange={(e) => { setForm({ ...form!, [k]: e.target.value }); setState({ kind: "edit" }); if (flagged?.has(k)) setFlagged(new Set([...flagged].filter((f) => f !== k))); }} />
    </label>
  );

  return (
    <div className="min-h-screen bg-bg px-4 py-10 grid place-items-start sm:place-items-center">
      <div className="w-full max-w-lg mx-auto rounded-2xl border border-border bg-surface p-6 shadow-sm" style={brand ? { borderTop: `4px solid ${brand}` } : undefined}>
        {q.isLoading && <Skeleton className="h-64 w-full" />}
        {q.isError && (
          <div className="text-center py-6">
            <CircleAlert className="size-6 mx-auto text-fg-subtle" />
            <p className="mt-3 font-medium">{(q.error instanceof ApiError && q.error.status === 404) ? T.de.notFound : T.de.error}</p>
            <p className="mt-1 text-[13px] text-fg-muted">{T.en.notFound}</p>
          </div>
        )}
        {d && (
          <>
            {d.hasLogo ? <img src={api.publicAddress.logoUrl(token)} alt={d.shop} className="max-h-12 max-w-[200px] object-contain" /> : <p className="text-[13px] font-semibold text-fg-muted">{d.shop}</p>}
            {state.kind === "saved" || d.status === "corrected" || d.status === "confirmed" ? (
              <div className="py-6 text-center">
                <CheckCircle2 className="size-8 mx-auto text-success" />
                <p className="mt-3 text-[17px] font-semibold">{state.kind === "saved" ? t.saved : t.closed}</p>
                <p className="mt-1 text-[13px] text-fg-muted">{state.kind === "saved" ? t.savedHint : t.closedHint}</p>
              </div>
            ) : d.status !== "open" ? (
              <div className="py-6 text-center">
                <CheckCircle2 className="size-8 mx-auto text-fg-subtle" />
                <p className="mt-3 text-[17px] font-semibold">{t.closed}</p>
                <p className="mt-1 text-[13px] text-fg-muted">{t.closedHint}</p>
              </div>
            ) : (
              <>
                <h1 className="mt-2 text-[22px] font-semibold tracking-tight flex items-center gap-2"><MapPin className="size-5 text-fg-subtle" /> {t.title}</h1>
                <p className="mt-1 text-[13.5px] text-fg-muted">{t.intro(d.orderNumber)}</p>
                {d.issues.length > 0 && (
                  <div className="mt-4 rounded-xl border border-warning/30 bg-warning-soft/40 px-3 py-2.5 text-[13px]">
                    <p className="font-medium">{t.issues}</p>
                    <ul className="mt-1 list-disc pl-5 text-fg-muted">{d.issues.map((i) => <li key={i}>{i}</li>)}</ul>
                  </div>
                )}
                {form && (
                  <form className="mt-5 space-y-3" onSubmit={(e) => { e.preventDefault(); send("check"); }}>
                    {field("company")}
                    <div className="grid grid-cols-2 gap-3">{field("firstName")}{field("lastName")}</div>
                    <div className="grid grid-cols-[1fr_96px] gap-3">{field("street")}{field("houseNumber")}</div>
                    {field("addition")}
                    {(dropPoint || flagged?.has("addition")) && <p className="-mt-2 text-[11.5px] text-fg-subtle">{t.postNumberHint}</p>}
                    <div className="grid grid-cols-[110px_1fr] gap-3">{field("zip")}{field("city")}</div>
                    <p className="text-[12px] text-fg-subtle">{t.country}: {d.address.country}</p>
                    {missing.size > 0 && <p className="text-[12.5px] text-danger">{t.required}</p>}
                    {submit.isError && <p className="text-[12.5px] text-danger">{t.error}</p>}

                    {state.kind === "suggestion" && (
                      <div className="rounded-xl border border-info/30 bg-info-soft/50 p-3 text-[13px]">
                        <p className="font-medium">{t.suggestion}</p>
                        <p className="mt-1">{state.address.street} {state.address.houseNumber}, {state.address.zip} {state.address.city}</p>
                        <div className="mt-3 flex flex-wrap gap-2">
                          <Button type="button" size="sm" variant="primary" loading={submit.isPending} style={brandStyle} onClick={() => { setForm(state.address); send("check", state.address); }}>{t.useSuggestion}</Button>
                          <Button type="button" size="sm" loading={submit.isPending} onClick={() => send("confirm")}>{t.keepMine}</Button>
                        </div>
                      </div>
                    )}
                    {state.kind === "unclear" && (
                      <div className="rounded-xl border border-warning/30 bg-warning-soft/40 p-3 text-[13px]">
                        <p className="font-medium">{t.unclear}</p>
                        {state.issues.length > 0 && <ul className="mt-1 list-disc pl-5 text-fg-muted">{state.issues.map((i) => <li key={i}>{i}</li>)}</ul>}
                        <p className="mt-1 text-fg-muted">{t.unclearHint}</p>
                        <Button type="button" size="sm" className="mt-3" loading={submit.isPending} onClick={() => send("confirm")}>{t.useAnyway}</Button>
                      </div>
                    )}
                    {state.kind === "edit" && (
                      <div className="pt-2 flex flex-wrap gap-2">
                        <Button type="submit" variant="primary" loading={submit.isPending} style={brandStyle}>{t.check}</Button>
                        <Button type="button" variant="ghost" disabled={submit.isPending} onClick={() => send("confirm", { ...form })}>{t.correct}</Button>
                      </div>
                    )}
                  </form>
                )}
              </>
            )}
            {(d.contact || d.footer) && (
              <div className="mt-6 pt-4 border-t border-border text-[12px] text-fg-subtle space-y-1">
                {d.contact && <p>{t.contact} <a className="underline" href={`mailto:${d.contact}`}>{d.contact}</a></p>}
                {d.footer && <p className="whitespace-pre-line">{d.footer}</p>}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
